import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Button, Input, Textarea } from '@material-tailwind/react';
import useDebounce from '../../hooks/useDebounce';

export default function PostUpload({ resetUpload, display, link }) {
  const [copied, setCopied] = useState(false);
  const [displaylink, setDisplayLink] = useState(link);
  const [uploadId, setUploadId] = useState("")
  const [title, setTitle] = useState("");
  const [maxTitleLength, setMaxTitleLength] = useState(100);
  const [titleError, setTitleError] = useState("");
  const [description, setDescription] = useState("");
  const [maxDescriptionLength, setMaxDescriptionLength] = useState(500);
  const [descriptionError, setDescriptionError] = useState("");
  const copyTimeoutRef = useRef();
  const cardRef = useRef();
  
  useEffect(() => {
    setDisplayLink(link);
    getUploadId();
  }, [link]);

  useEffect(() => {
    getMaxLengths();
  }, [])

  async function getMaxLengths() {
    const response = await fetch("https://uploads.jellyo.net/api/upload/maxdetaillength", { credentials: "include"});
    const responseData = await response.json();
    setMaxTitleLength(responseData.maxTitleLength);
    setMaxDescriptionLength(responseData.maxDescriptionLength)
  }

  function getUploadId() {
    if (link) {
      const urlObj = new URL(link);
      setUploadId(urlObj.pathname.substring(1));
    }
  }

  const debouncedUpdateField = useDebounce(async (key, value) => {
    try {
      const response = await fetch("https://uploads.jellyo.net/api/upload/update", {
        credentials: "include",
        method: "POST", 
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          uploadEntry: key,
          update: value,
          uploadId: uploadId,
        })
      });
      const result = await response.json();
      
      if (result.status !== "success") {
        if (key === "uploadTitle") setTitleError(result.message);
        if (key === "uploadDescription") setDescriptionError(result.message);
      }
    } catch (error) {
      console.error('Error updating field:', error);
    }
  }, 1000);

  function clickToCopy() {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)

    setCopied(true);
    setDisplayLink("Copied to clipboard!");
    navigator.clipboard.writeText(link)

    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
      setDisplayLink(link);
    }, 1500)
  }

  function handleChange(key, value) {
    if ((key === "uploadTitle" && value.length > 100) || (key === "uploadDescription" && value.length > 500)) {
      return;
    }
  
    if (key === "uploadTitle") setTitle(value);
    if (key === "uploadDescription") setDescription(value);
  
    debouncedUpdateField(key, value);
  }

  function handleUploadAgain() {
    resetUpload();
    setTitle("");
    setDescription("");
  }

  return (
    <div ref={cardRef} className={`${!display ? "opacity-0 translate-x-[210%]" : "opacity-100"} absolute max-w-[50rem] w-full min-h-[30rem] h-max m-4 mt-8 px-7 py-5 bg-offwhite rounded-xl shadow-all flex flex-col justify-between transition-all duration-700`}>
      <div className='m-4 mb-8 text-center'>
        <h1 className='mb-1 text-xl md:text-3xl text-gray-800 font-medium'>Thank you for using Jellyo Uploads!</h1>
        <p className={`px-4 text-sm md:text-base text-gray-600`}>Your file is ready to share, click the link below to copy it</p>
      </div>

      <div className='w-full mb-4 flex flex-col'>
        <div className='flex'>
          <div onClick={clickToCopy} className={`${copied ? "border-green-400 text-green-400 font-medium select-none" : ""} w-10/12 sm:w-11/12 px-3 py-2.5 border-2 border-r-0 border-solid border-blue-gray-100 rounded-lg rounded-r-none bg-gray-200 cursor-pointer transition-all truncate text-clip`}>
            <span className=' '>{displaylink}</span>
          </div>
          <div onClick={() => window.open(link, "_blank")} className={`${copied ? "border-green-400" : ""} w-2/12 sm:w-1/12 border-2 border-l-0 border-solid border-blue-gray-100 rounded-lg rounded-l-none bg-gray-400 flex justify-center items-center cursor-pointer`}>
            <i className='bx bx-link-external text-2xl'></i>
          </div>
        </div>
      </div>

      <h3 className='text-lg'>Upload Details</h3>

      <div className='relative w-full flex flex-col mt-1 mb-8'>
        <Input 
          type="text"
          label='Title'
          value={title}
          onChange={(e) => handleChange("uploadTitle", e.target.value)}
          color='blue-gray'
          maxLength={maxTitleLength}
          className='w-full px-3 py-2.5 border border-blue-gray-200 border-solid rounded-[7px]'
          containerProps={{
            className: "!min-w-0",
          }}
        />
        <div className='absolute -bottom-6 w-full flex justify-between'>
          <span className={`text-sm text-red-600`}>{titleError}</span>
          <span className='text-gray-600'>{title.length}/{maxTitleLength}</span>
        </div>
      </div>

      <div className='relative w-full mb-8'>
        <Textarea 
          label='Description' 
          value={description}
          maxLength={maxDescriptionLength}
          className='mb-0'
          onChange={(e) => handleChange("uploadDescription", e.target.value)}
        />
        <div className='absolute -bottom-4 w-full flex justify-between'>
          <span className={`text-sm text-red-600`}>{descriptionError}</span>
          <span className='text-gray-600'>{description.length}/{maxDescriptionLength}</span>
        </div>
      </div>

      <Button onClick={handleUploadAgain} className='w-full h-11 rounded-lg flex justify-center items-center text-offwhite'>
        <span>Upload Again</span>
      </Button>
    </div>
  )
}
